import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  getCurrentCycleStockCountControl,
  getStockCountCoverageReport,
  getTodayStockCountControl,
} from '../../../services/StockCountService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { formatStockQuantity } from '../../../utils/stockQuantity';

const RESOLVED_STATUSES = new Set(['COUNTED', 'PENDING_APPROVAL', 'SKIPPED']);

const STATUS_CONFIG = {
  PENDING: { label: 'Pendiente', color: 'default' },
  NEEDS_RECOUNT: { label: 'Volver a contar', color: 'warning' },
  COUNTED: { label: 'Contado', color: 'success' },
  PENDING_APPROVAL: { label: 'En revisión', color: 'info' },
  SKIPPED: { label: 'Omitido', color: 'default' },
};

const formatDifference = (difference) => {
  if (difference === null || difference === undefined) return '—';
  return difference > 0 ? `+${formatStockQuantity(difference)}` : formatStockQuantity(difference);
};

const getResult = (item) => {
  if (!item.attemptCount) return { label: 'Pendiente', color: 'default' };
  if (item.status === 'COUNTED') {
    const attemptLabel = item.attemptCount === 1 ? '1.er intento' : '2.º intento';
    return { label: `Coincide · ${attemptLabel}`, color: 'success' };
  }
  if (item.status === 'NEEDS_RECOUNT') {
    return { label: `No coincide · ${formatDifference(item.difference)}`, color: 'warning' };
  }
  if (item.status === 'PENDING_APPROVAL') {
    return { label: `No coincide · ${formatDifference(item.difference)}`, color: 'error' };
  }
  return { label: STATUS_CONFIG[item.status]?.label || item.status, color: STATUS_CONFIG[item.status]?.color || 'default' };
};

const StockCountDetailTable = ({ items, includeDate = false }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          {includeDate && <TableCell>Fecha</TableCell>}
          <TableCell>Sucursal</TableCell>
          <TableCell>Ítem</TableCell>
          <TableCell>Tipo</TableCell>
          <TableCell align="right">Contado</TableCell>
          <TableCell align="right">Sistema</TableCell>
          <TableCell>Resultado</TableCell>
          <TableCell>Intentos</TableCell>
          <TableCell>Quién / cuándo</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item) => {
          const result = getResult(item);
          const countedBy = [...new Set(item.attempts.map((attempt) => attempt.countedByEmail))];
          const latestAttempt = item.attempts[item.attempts.length - 1];
          return (
            <TableRow key={item.id} hover>
              {includeDate && <TableCell>{item.sessionDate}</TableCell>}
              <TableCell>{item.locationName}</TableCell>
              <TableCell>{item.referenceName || `#${item.referenceId}`}</TableCell>
              <TableCell>{item.referenceTypeLabel}</TableCell>
              <TableCell align="right">{formatStockQuantity(item.countedStock) ?? '—'}</TableCell>
              <TableCell align="right">{formatStockQuantity(item.systemStock)}</TableCell>
              <TableCell><Chip size="small" label={result.label} color={result.color} /></TableCell>
              <TableCell>{item.attempts.length ? item.attempts.map((attempt) => formatStockQuantity(attempt.countedStock)).join(' → ') : '—'}</TableCell>
              <TableCell>
                {countedBy.length ? (
                  <>
                    <Typography variant="body2">{countedBy.join(' · ')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {latestAttempt ? new Date(latestAttempt.countedAt).toLocaleString() : ''}
                    </Typography>
                  </>
                ) : '—'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const StockCountControlPage = () => {
  const [sessions, setSessions] = useState([]);
  const [cycleSessions, setCycleSessions] = useState([]);
  const [coverage, setCoverage] = useState({ branches: [] });
  const [period, setPeriod] = useState('today');
  // null significa "todas"; un array permite elegir una, varias o ninguna.
  const [selectedLocationIds, setSelectedLocationIds] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const loadControl = async () => {
      try {
        setLoading(true);
        const [todayData, coverageData, cycleData] = await Promise.all([
          getTodayStockCountControl(),
          getStockCountCoverageReport(),
          getCurrentCycleStockCountControl(),
        ]);
        if (active) {
          setSessions(todayData?.sessions || []);
          setCoverage(coverageData || { branches: [] });
          setCycleSessions(cycleData?.sessions || []);
        }
      } catch (error) {
        console.error('Error loading stock count control', error);
        SnackbarUtils.error('No pudimos cargar el control de conteo');
      } finally {
        if (active) setLoading(false);
      }
    };
    loadControl();
    return () => { active = false; };
  }, []);

  const branchOptions = useMemo(() => {
    const branches = new Map();
    (coverage.branches || []).forEach((branch) => branches.set(branch.locationId, branch.locationName));
    sessions.forEach((session) => branches.set(session.locationId, session.locationName));
    cycleSessions.forEach((session) => branches.set(session.locationId, session.locationName));
    return [...branches].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [coverage, sessions, cycleSessions]);

  const activeLocationIds = selectedLocationIds === null
    ? branchOptions.map((branch) => branch.id)
    : selectedLocationIds;
  const isAllLocationsSelected = selectedLocationIds === null;
  const filteredSessions = useMemo(
    () => sessions.filter((session) => activeLocationIds.includes(session.locationId)),
    [sessions, activeLocationIds]
  );
  const filteredCycleSessions = useMemo(
    () => cycleSessions.filter((session) => activeLocationIds.includes(session.locationId)),
    [cycleSessions, activeLocationIds]
  );

  const { items, total, resolved, attempted, differences } = useMemo(() => {
    const allItems = filteredSessions.flatMap((session) => session.items.map((item) => ({ ...item, locationName: session.locationName })));
    return {
      items: allItems,
      total: allItems.length,
      resolved: allItems.filter((item) => RESOLVED_STATUSES.has(item.status)).length,
      attempted: allItems.filter((item) => item.attemptCount > 0).length,
      differences: allItems.filter((item) => item.difference !== null && item.difference !== undefined && Math.abs(item.difference) >= 0.01).length,
    };
  }, [filteredSessions]);
  const cycleItems = useMemo(
    () => filteredCycleSessions.flatMap((session) => session.items.map((item) => ({
      ...item,
      locationName: session.locationName,
      sessionDate: session.sessionDate,
    }))),
    [filteredCycleSessions]
  );

  const progress = total ? (resolved / total) * 100 : 0;
  const cycle = useMemo(() => {
    const branches = (coverage.branches || []).filter((branch) => activeLocationIds.includes(branch.locationId));
    const universeSize = branches.reduce((sum, branch) => sum + branch.universeSize, 0);
    const covered = branches.reduce((sum, branch) => sum + branch.coveredInCycle, 0);
    const durations = [...new Set(branches.map((branch) => branch.fullCycleDays))];
    return {
      branches,
      universeSize,
      covered,
      percent: universeSize ? (covered / universeSize) * 100 : 0,
      durationLabel: durations.length === 1 ? `${durations[0]} días` : 'duración por sucursal',
    };
  }, [coverage, activeLocationIds]);

  const handleLocationsChange = (event) => {
    const values = event.target.value;
    if (values.includes('__all__')) {
      setSelectedLocationIds(isAllLocationsSelected ? [] : null);
      return;
    }
    setSelectedLocationIds(values.map(Number));
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <ToggleButtonGroup
        value={period}
        exclusive
        size="small"
        onChange={(_, value) => value && setPeriod(value)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="today">Hoy</ToggleButton>
        <ToggleButton value="cycle">Ciclo completo</ToggleButton>
      </ToggleButtonGroup>

      <FormControl size="small" sx={{ minWidth: 260, ml: { sm: 2 }, mb: 2 }}>
        <InputLabel id="stock-count-branches-label">Sucursales</InputLabel>
        <Select
          labelId="stock-count-branches-label"
          multiple
          value={activeLocationIds}
          label="Sucursales"
          onChange={handleLocationsChange}
          renderValue={(selected) => {
            if (isAllLocationsSelected) return 'Todas las sucursales';
            if (!selected.length) return 'Sin sucursales';
            return branchOptions.filter((branch) => selected.includes(branch.id)).map((branch) => branch.name).join(', ');
          }}
        >
          <MenuItem value="__all__">
            <Checkbox checked={isAllLocationsSelected} indeterminate={!isAllLocationsSelected && activeLocationIds.length > 0} />
            <ListItemText primary="Todas las sucursales" />
          </MenuItem>
          {branchOptions.map((branch) => (
            <MenuItem key={branch.id} value={branch.id}>
              <Checkbox checked={activeLocationIds.includes(branch.id)} />
              <ListItemText primary={branch.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {period === 'today' ? (
        <>
          {!filteredSessions.length ? (
            <Alert severity="info">No hay sesiones de conteo iniciadas hoy para las sucursales seleccionadas.</Alert>
          ) : (
            <>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                      <Typography variant="subtitle1">Avance del conteo de hoy</Typography>
                      <Typography variant="body2" color="text.secondary">{resolved} de {total} ítems resueltos ({Math.round(progress)}%)</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={Math.min(progress, 100)} sx={{ height: 9, borderRadius: 4 }} />
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`${attempted} contados`} color="primary" variant="outlined" />
                    <Chip label={`${differences} con diferencia`} color={differences ? 'warning' : 'success'} variant="outlined" />
                    <Chip label={`${filteredSessions.length} sucursal${filteredSessions.length === 1 ? '' : 'es'}`} variant="outlined" />
                  </Stack>
                </Stack>
              </Paper>

              <StockCountDetailTable items={items} />
            </>
          )}
        </>
      ) : !cycle.branches.length ? (
        <Alert severity="info">No hay sucursales con un ciclo de conteo activo.</Alert>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                  <Typography variant="subtitle1">Avance del ciclo de conteo</Typography>
                  <Typography variant="body2" color="text.secondary">{cycle.covered} de {cycle.universeSize} ítems cubiertos ({Math.round(cycle.percent)}%)</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={Math.min(cycle.percent, 100)} sx={{ height: 9, borderRadius: 4 }} />
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Ciclo: ${cycle.durationLabel}`} color="primary" variant="outlined" />
                <Chip label={`${cycle.branches.length} sucursal${cycle.branches.length === 1 ? '' : 'es'}`} variant="outlined" />
              </Stack>
            </Stack>
          </Paper>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sucursal</TableCell>
                  <TableCell>Inicio del ciclo</TableCell>
                  <TableCell align="right">Días del ciclo</TableCell>
                  <TableCell align="right">Ítems cubiertos</TableCell>
                  <TableCell align="right">Universo</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Progreso</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cycle.branches.map((branch) => (
                  <TableRow key={branch.locationId} hover>
                    <TableCell>{branch.locationName}</TableCell>
                    <TableCell>{branch.cycleStartDate}</TableCell>
                    <TableCell align="right">{branch.fullCycleDays}</TableCell>
                    <TableCell align="right">{branch.coveredInCycle}</TableCell>
                    <TableCell align="right">{branch.universeSize}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LinearProgress variant="determinate" value={Math.min(branch.coveragePercent, 100)} sx={{ flex: 1, height: 7, borderRadius: 4 }} />
                        <Typography variant="caption" sx={{ minWidth: 32 }}>{Math.round(branch.coveragePercent)}%</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
            Detalle del ciclo actual
          </Typography>
          {!cycleItems.length ? (
            <Alert severity="info">Todavía no hay sesiones de conteo dentro de este ciclo para las sucursales seleccionadas.</Alert>
          ) : (
            <StockCountDetailTable items={cycleItems} includeDate />
          )}
        </>
      )}
    </Box>
  );
};

export default StockCountControlPage;
